import { useEffect, useState } from "react";

import { duration } from "@/shared/lib";

/**
 * Скелетон загрузки. Figma: `State · Loading Dashboard` (28:2) — ровно три
 * прямоугольника 350×120, 350×300 и 350×108.
 *
 * Формы взяты из макета, а не придуманы: скелетон, не совпадающий с будущей
 * раскладкой, даёт скачок контента в момент загрузки — ровно то, ради чего
 * скелетоны и делают.
 *
 * Движение (FE-13):
 *  - пульсация заменена бегущим бликом: пульс мигает всей плашкой и на
 *    тёмной теме читается как дефект отрисовки, блик — как ожидание;
 *  - первые 150 мс скелетон не показывается вовсе. На быстром ответе (а в
 *    Telegram запрос к своему же серверу нередко укладывается в 80 мс)
 *    пользователь иначе видит вспышку заглушки — она раздражает сильнее,
 *    чем пустой кадр.
 */
const BLOCKS = [120, 300, 108] as const;

interface ScreenSkeletonProps {
  /** Высоты блоков в пикселях. По умолчанию — как на макете главной. */
  blocks?: readonly number[];
}

const REVEAL_MS = 150;

export function ScreenSkeleton({ blocks = BLOCKS }: ScreenSkeletonProps) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setVisible(true);
    }, REVEAL_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, []);

  return (
    <div
      className="flex flex-col gap-3 px-5 py-6 transition-opacity ease-standard"
      style={{
        opacity: visible ? 1 : 0,
        transitionDuration: `${String(duration("base"))}ms`,
      }}
      role="status"
      aria-busy="true"
    >
      {blocks.map((height, index) => (
        <div
          key={`${String(height)}-${String(index)}`}
          className="shimmer panel-surface rounded-lg"
          style={{ height, animationDelay: `calc(${String(index)} * var(--motion-stagger))` }}
        />
      ))}
    </div>
  );
}
